import { useEffect, useState } from 'react';
import { fetchAuditQueue } from '../services/auth';

/**
 * AuditQueue - Read-only review queue (spec audit-review-console R1).
 *
 * Paginated table of the tenant's audit runs with status/pack/date filters.
 * Rows navigate to the detail view. There is no write action anywhere in
 * this view — v1 is view-only by frozen product decision.
 */

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'corrected', label: 'Corrected' },
];

const PAGE_SIZE = 20;

function RecommendationBadge({ value }) {
  if (!value) return <span className="badge badge-muted">—</span>;
  const cls =
    value === 'accept' ? 'badge-accept' :
    value === 'reject' ? 'badge-reject' :
    'badge-downgrade';
  return <span className={`badge ${cls}`}>{value}</span>;
}

function RunStatusBadge({ value }) {
  if (value === 'FAILED') return <span className="badge badge-failed">FAILED</span>;
  if (value === 'PENDING') return <span className="badge badge-muted">PENDING</span>;
  return <span className="badge badge-completed">COMPLETED</span>;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

export function AuditQueue({ onOpenTrace, refreshKey }) {
  const [filters, setFilters] = useState({ status: 'all', pack: '', from: '', to: '' });
  const [tokenStack, setTokenStack] = useState([null]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const startToken = tokenStack[tokenStack.length - 1];

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchAuditQueue({
          status: filters.status,
          pack: filters.pack || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined,
          limit: PAGE_SIZE,
          nextToken: startToken || undefined,
        });
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [filters, startToken, refreshKey]);

  function updateFilter(patch) {
    setTokenStack([null]); // any filter change restarts pagination
    setFilters((prev) => ({ ...prev, ...patch }));
  }

  function goNext() {
    if (data?.next_token) setTokenStack((stack) => [...stack, data.next_token]);
  }

  function goPrev() {
    if (tokenStack.length > 1) setTokenStack((stack) => stack.slice(0, -1));
  }

  const items = data?.items || [];
  const hasPrev = tokenStack.length > 1;
  const hasNext = Boolean(data?.next_token);

  return (
    <section className="queue">
      <div className="queue-toolbar">
        <h2>Review queue</h2>
      </div>

      <div className="filter-bar">
        <label className="filter">
          <span>Status</span>
          <select
            value={filters.status}
            onChange={(e) => updateFilter({ status: e.target.value })}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
        <label className="filter">
          <span>Pack</span>
          <input
            type="text"
            placeholder="e.g. medica"
            value={filters.pack}
            onChange={(e) => updateFilter({ pack: e.target.value })}
          />
        </label>
        <label className="filter">
          <span>From</span>
          <input
            type="date"
            value={filters.from}
            onChange={(e) => updateFilter({ from: e.target.value })}
          />
        </label>
        <label className="filter">
          <span>To</span>
          <input
            type="date"
            value={filters.to}
            onChange={(e) => updateFilter({ to: e.target.value })}
          />
        </label>
      </div>

      {loading && (
        <div className="queue-state loading">
          <span className="spinner"></span> Loading audit runs…
        </div>
      )}
      {error && <div className="queue-state error">Error: {error}</div>}
      {!loading && !error && items.length === 0 && (
        <div className="queue-state empty">
          No audit runs match the current filters.
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="table-wrap">
          <table className="queue-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Trace</th>
                <th>Pack</th>
                <th>Recommendation</th>
                <th className="num">Non-compliant</th>
                <th>Review</th>
                <th>Run</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.trace_id}
                  className="queue-row"
                  onClick={() => onOpenTrace(item.trace_id)}
                >
                  <td className="cell-date">{formatDate(item.created_at)}</td>
                  <td className="cell-trace mono">{item.trace_id}</td>
                  <td>{item.pack || '—'}</td>
                  <td>
                    <RecommendationBadge value={item.recommendation} />
                    {item.downgrade_to && (
                      <span className="downgrade-target"> → {item.downgrade_to}</span>
                    )}
                  </td>
                  <td className={`num cell-count ${item.non_compliant > 0 ? 'count-hot' : ''}`}>
                    {item.non_compliant ?? 0}
                  </td>
                  <td><span className="pill">{item.review_status || '—'}</span></td>
                  <td><RunStatusBadge value={item.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pager">
        <button className="btn-secondary" onClick={goPrev} disabled={!hasPrev}>
          ← Previous
        </button>
        <span className="pager-info mono">
          {data ? `${data.count} run${data.count === 1 ? '' : 's'} on this page` : ''}
        </span>
        <button className="btn-secondary" onClick={goNext} disabled={!hasNext}>
          Next →
        </button>
      </div>
    </section>
  );
}
